import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Sprout, Snowflake, FlaskConical, Microscope, Leaf } from "lucide-react";
import { HERO, BENEFITS_SHORT, PROCESS_STEPS } from "../mock";

const ICONS = { Sprout, Snowflake, FlaskConical, Microscope, Leaf };

export default function HomePage() {
  return (
    <div className="bg-[#f4f0e6] text-[#3a3a32]">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1618100790047-3da47dcd5727"
            alt="Ancient olive grove at golden hour"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c44]/55 via-[#0f2c44]/45 to-[#0f2c44]/65" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center text-[#f4f0e6] py-24">
          <h1
            className="text-4xl md:text-6xl lg:text-7xl leading-[1.05] font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {HERO.title}
          </h1>
          <p
            className="mt-6 text-xl md:text-2xl text-[#f4f0e6]/90 font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {HERO.subtitle}
          </p>

          <div className="mt-10 space-y-3 max-w-2xl mx-auto">
            {HERO.bullets.map((b, i) => (
              <p key={i} className="text-base md:text-lg text-[#f4f0e6]/85 leading-relaxed">
                {b}
              </p>
            ))}
          </div>

          <div className="mt-10 inline-block text-left bg-[#1e3a52]/60 backdrop-blur-sm border border-[#dcd6c4]/20 rounded-md px-6 py-5">
            <div className="text-xs tracking-[0.3em] uppercase text-[#dcd6c4] mb-3">2025 Harvest</div>
            <ul className="space-y-1.5">
              {HERO.harvest.map((h, i) => (
                <li key={i} className="flex items-center gap-2 text-sm md:text-base">
                  <Check size={16} className="text-[#d4a857] flex-shrink-0" strokeWidth={2.5} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10">
            <Link
              to="/waitlist"
              className="inline-flex items-center gap-2 bg-[#f4f0e6] hover:bg-white text-[#1e3a52] px-8 py-3 rounded-sm text-lg transition-colors"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Reserve the 2025 Harvest <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* 45x SECTION */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24 text-center">
        <p className="text-xs tracking-[0.4em] uppercase text-[#4a7ba1] mb-4">A different category of olive oil</p>
        <h2
          className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight text-[#3a3a32]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Up to <span className="text-[#4a7ba1]">45× more polyphenols</span><br />than regular olive oil.
        </h2>
        <p className="mt-6 max-w-2xl mx-auto text-[#5a5a52] leading-relaxed">
          Most supermarket oils contain 50–150 mg/kg of polyphenols. Fountain of Youth delivers 2,236 mg/kg — verified by independent laboratories using the analytical method recognised by the EFSA and the IOC.
        </p>
      </section>

      {/* WHY POLYPHENOLS */}
      <section className="bg-[#ece6d6]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs tracking-[0.4em] uppercase text-[#4a7ba1] mb-4">Why polyphenols?</p>
            <h2
              className="text-3xl md:text-5xl font-light leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Mediterranean wisdom, measured by modern science.
            </h2>
            <ul className="mt-8 space-y-3">
              {BENEFITS_SHORT.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[#3a3a32]">
                  <Check size={18} className="text-[#4a7ba1] mt-1 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-lg">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/health-benefits"
                className="inline-flex items-center gap-2 bg-[#1e3a52] hover:bg-[#142a3f] text-[#f4f0e6] px-6 py-2.5 rounded-sm transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
              >
                Discover the Benefits <ArrowRight size={16} />
              </Link>
              <Link
                to="/waitlist"
                className="inline-flex items-center gap-2 border border-[#1e3a52] text-[#1e3a52] hover:bg-[#1e3a52] hover:text-[#f4f0e6] px-6 py-2.5 rounded-sm transition-colors"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}
              >
                Reserve Yours
              </Link>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5"
              alt="Premium olive oil"
              className="w-full h-[520px] object-cover rounded-sm shadow-xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-[#f4f0e6] border border-[#4a7ba1]/20 rounded-sm px-6 py-5 shadow-lg hidden md:block">
              <div className="text-3xl text-[#1e3a52]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>2,236</div>
              <div className="text-xs uppercase tracking-widest text-[#4a7ba1] mt-1">mg/kg polyphenols</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs tracking-[0.4em] uppercase text-[#4a7ba1] mb-4">Our proprietary process</p>
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            From our groves to your home — every variable controlled.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
          {PROCESS_STEPS.map((s, i) => {
            const Icon = ICONS[s.icon] || Leaf;
            return (
              <div key={i} className="text-center group">
                <div className="w-20 h-20 mx-auto rounded-full border border-[#4a7ba1]/30 grid place-items-center bg-[#ece6d6] group-hover:bg-[#4a7ba1] group-hover:border-[#4a7ba1] transition-colors">
                  <Icon size={32} className="text-[#4a7ba1] group-hover:text-[#f4f0e6] transition-colors" strokeWidth={1.4} />
                </div>
                <h3 className="mt-5 text-lg text-[#3a3a32]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.title}</h3>
                <p className="mt-2 text-sm text-[#5a5a52] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-14">
          <Link
            to="/about"
            className="inline-flex items-center gap-2 text-[#1e3a52] border-b border-[#4a7ba1] hover:text-[#4a7ba1] pb-1"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px" }}
          >
            Learn more about our process <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* SCIENCE STRIP */}
      <section className="relative">
        <img
          src="https://images.unsplash.com/photo-1614935151651-0bea6508db6b"
          alt="Laboratory analysis"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0f2c44]/75" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-24 text-center text-[#f4f0e6]">
          <p className="text-xs tracking-[0.4em] uppercase text-[#d4a857] mb-4">Backed by science</p>
          <h2 className="text-3xl md:text-5xl font-light leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Over 2,000 peer-reviewed studies. One verified concentration.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[#f4f0e6]/85 leading-relaxed">
            Supported by an EU health claim and decades of clinical research, high-phenolic olive oil is one of the most studied functional foods in modern nutrition.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/health-benefits" className="bg-[#f4f0e6] hover:bg-white text-[#1e3a52] px-7 py-3 rounded-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}>
              Explore the Evidence
            </Link>
            <Link to="/analysis" className="border border-[#f4f0e6] hover:bg-[#f4f0e6]/10 text-[#f4f0e6] px-7 py-3 rounded-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}>
              View 2025 Analysis
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
